import * as React from 'react'
import { createPortal } from 'react-dom'

import { cn } from '@shared/ui/lib/utils'
import './anchored-popover.css'

type PopoverHorizontal = 'center' | 'left' | 'right'
type PopoverVertical = 'bottom' | 'top'

interface PopoverOrigin {
  readonly horizontal: PopoverHorizontal
  readonly vertical: PopoverVertical
}

export interface AnchoredPopoverProps
  extends Omit<React.ComponentPropsWithoutRef<'div'>, 'children'> {
  readonly anchorEl: HTMLElement | null
  readonly anchorOrigin?: PopoverOrigin
  readonly children: React.ReactNode
  readonly disablePortal?: boolean
  readonly offset?: number
  readonly onClose: () => void
  readonly open?: boolean
  readonly transformOrigin?: PopoverOrigin
  readonly transitionDuration?: number
}

function getCoordinate(rect: DOMRect, origin: PopoverOrigin) {
  const top = origin.vertical === 'bottom' ? rect.bottom : rect.top
  const left =
    origin.horizontal === 'center'
      ? rect.left + rect.width / 2
      : origin.horizontal === 'right'
        ? rect.right
        : rect.left

  return { left, top }
}

function getTransform(origin: PopoverOrigin) {
  const x =
    origin.horizontal === 'center'
      ? '-50%'
      : origin.horizontal === 'right'
        ? '-100%'
        : '0'
  const y = origin.vertical === 'top' ? '-100%' : '0'

  return `translate(${x}, ${y})`
}

function getPositionStyle(
  anchorEl: HTMLElement,
  anchorOrigin: PopoverOrigin,
  transformOrigin: PopoverOrigin,
  offset: number,
) {
  const rect = anchorEl.getBoundingClientRect()
  const position = getCoordinate(rect, anchorOrigin)
  const verticalOffset = anchorOrigin.vertical === 'bottom' ? offset : -offset

  return {
    left: position.left,
    top: position.top + verticalOffset,
    transform: getTransform(transformOrigin),
  } satisfies React.CSSProperties
}

export function AnchoredPopover({
  anchorEl,
  anchorOrigin = { horizontal: 'left', vertical: 'bottom' },
  children,
  className,
  disablePortal = false,
  offset = 8,
  onClose,
  open = Boolean(anchorEl),
  style,
  transformOrigin = { horizontal: 'left', vertical: 'top' },
  transitionDuration,
  ...props
}: AnchoredPopoverProps) {
  const popoverRef = React.useRef<HTMLDivElement | null>(null)
  const [layoutRevision, setLayoutRevision] = React.useState(0)

  React.useLayoutEffect(() => {
    if (!anchorEl || !open) {
      return undefined
    }

    const updatePosition = () => {
      setLayoutRevision((currentRevision) => currentRevision + 1)
    }

    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)

    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [anchorEl, open])

  React.useEffect(() => {
    if (!anchorEl || !open) {
      return undefined
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target

      if (!(target instanceof Node)) {
        return
      }

      if (anchorEl.contains(target) || popoverRef.current?.contains(target)) {
        return
      }

      onClose()
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [anchorEl, onClose, open])

  if (!anchorEl || !open) {
    return null
  }

  void layoutRevision
  const position = getPositionStyle(anchorEl, anchorOrigin, transformOrigin, offset)

  const content = (
    <div
      className={cn('fixed outline-none', className)}
      data-slot="anchored-popover"
      ref={popoverRef}
      role="dialog"
      style={{
        ...position,
        transitionDuration:
          typeof transitionDuration === 'number' ? `${transitionDuration}ms` : undefined,
        ...style,
      }}
      tabIndex={-1}
      {...props}
    >
      {children}
    </div>
  )

  if (disablePortal) {
    return content
  }

  return createPortal(content, document.body)
}

export type { PopoverHorizontal, PopoverOrigin, PopoverVertical }
